/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CircleInvitation as Invitation } from "@/types/safecircle";
import { useCircleInvitations } from "@/hooks/useCircleInvitations";
import { MODE_CONFIG, PRIVACY_DESCRIPTIONS } from "@/utils/safecircle/constants";
import { useCircleMembers } from "@/hooks/useCircleMembers";
import { useAuth } from "@/contexts/AuthContext";
import { AVATAR_COLORS } from "@/utils/safecircle/constants";

export function CircleInvitationModal() {
  const { t } = useTranslation();
  const { incoming, accept, decline } = useCircleInvitations();
  const [active, setActive] = useState<Invitation | null>(null);

  useEffect(() => {
    if (incoming.length > 0 && !active) {
      setActive(incoming[0]);
    }
    if (incoming.length === 0 && active) {
      setActive(null);
    }
  }, [incoming, active]);

  return (
    <AnimatePresence>
      {active && (
        <CircleInvitationDialog
          invitation={active}
          onAccept={async () => {
            await accept(active.invitationId);
            setActive(null);
          }}
          onDecline={async () => {
            await decline(active.invitationId);
            setActive(null);
          }}
        />
      )}
    </AnimatePresence>
  );
}

export function CircleInvitationDialog({
  invitation,
  onAccept,
  onDecline,
}: {
  invitation: Invitation;
  onAccept: () => void | Promise<void>;
  onDecline: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // If the invitee is accepting, we mirror the member into THEIR own SafeCircle
  // profile list is not a concept here — just the circle-of-the-inviter on
  // their device. For the invitee we'll create a parallel "shadow" entry later.
  void user;
  void useCircleMembers; // silence unused in this file
  const modeCfg = MODE_CONFIG[invitation.mode];
  const avatarColor = useMemo(
    () => AVATAR_COLORS[invitation.fromName.length % AVATAR_COLORS.length],
    [invitation.fromName],
  );

  return (
    <Dialog open onOpenChange={() => onDecline()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary" />
            {t("safeCircle.invitation.title")}
          </DialogTitle>
          <DialogDescription>
            {t("safeCircle.invitation.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
              style={{
                backgroundColor: avatarColor + "25",
                color: avatarColor,
              }}
            >
              {invitation.fromName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {t("safeCircle.invitation.fromSomeone", {
                  name: invitation.fromName,
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("safeCircle.invitation.forProfile", {
                  profile: invitation.fromProfileName,
                })}
              </p>
            </div>
          </div>

          <div className="glass-card p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <span
                className={`px-2 py-0.5 rounded-full ${modeCfg.bgClass} ${modeCfg.textClass}`}
              >
                {modeCfg.emoji} {modeCfg.label}
              </span>
              <span className="text-muted-foreground">
                {t(`safeCircle.circle.role${capitalize(invitation.circleRole)}`)}
              </span>
            </div>
            <p className="text-[12px] text-muted-foreground">
              {t("safeCircle.invitation.privacyDisclosure")}
            </p>
            <p className="text-[12px] text-muted-foreground">
              {t(PRIVACY_DESCRIPTIONS[invitation.privacyLevel])}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onDecline()}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-muted/40 hover:bg-muted/60 flex items-center justify-center gap-1"
            >
              <X size={14} /> {t("safeCircle.invitation.decline")}
            </button>
            <button
              onClick={() => onAccept()}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium gradient-bg text-primary-foreground flex items-center justify-center gap-1"
            >
              <Check size={14} /> {t("safeCircle.invitation.accept")}
            </button>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}

function capitalize(input: string) {
  if (input === "trusted_contact") return "Trusted";
  return input.charAt(0).toUpperCase() + input.slice(1);
}

export default CircleInvitationModal;
